import { useMutation } from '@tanstack/react-query'
import { assert } from '@/utils'
import { CHAIN_CONFIG, CHAIN_INFO_LIST } from '@/config'
import {
  convertDenomToMicroDenom,
  generateIbcTimeoutTimestamp,
  IbcReturnType,
  TX_MSG,
  simulateIbcTransaction2,
  calculateFeeWithMinimalDenom,
  signIbcTransaction2
} from '@/wallet-utils'
import { MutationParameters } from './types'
import { useStake } from '../../StakeProvider'
import { useSelectedChain, useStrideChain } from '@/contexts/wallet'
import { flushSync } from 'react-dom'
import { useSelectedDenom } from '../../../../contexts/denom'

const useSignIbcTransferMutation = ({
  tokenizeSharesDenom,
  tokenizeSharesTokenValue,
  setIbcTransferRaw
}: MutationParameters) => {
  const { address: strideAddress } = useStrideChain()

  const {
    address: selectedAddress,
    getSigningStargateClient,
    getAccount,
    getOfflineSigner,
    wallet
  } = useSelectedChain()

  const [denom] = useSelectedDenom()

  const { lsm } = useStake()

  const handleMutation = async (): Promise<IbcReturnType> => {
    assert(strideAddress, 'You are unable to send token without connecting your wallet.')

    assert(selectedAddress, `You are unable to send token without connecting your ${denom} wallet.`)

    assert(wallet, `You are unable to send token without your ${denom} wallet instance.`)

    // Use the prefilled data from the transaction history if available.
    // Otherwise, we'll default to the data set in `useBroadcastTokenizeShares`.
    const tokenizeSharesDenomToIbc = lsm.txHistoryTokenizedDenom || tokenizeSharesDenom

    assert(tokenizeSharesDenomToIbc, 'Unable to ibc transfer without `tokenizeShareRecordId`.')

    const minimalDenom = CHAIN_INFO_LIST[denom].stakeCurrency.coinMinimalDenom

    // `tokenizeSharesTokenValue` is the accurate token value that was tokenized set by `useBroadcastTokenizeShares`.
    // Otherwise, we'll use `lsm.amount` which means that the user continued lsm from the transaction history.
    const amountInMicroDenom = tokenizeSharesTokenValue
      ? tokenizeSharesTokenValue
      : convertDenomToMicroDenom(lsm.amount, minimalDenom)

    const msgTransfer = {
      typeUrl: TX_MSG.MsgTransfer,
      value: {
        sourcePort: 'transfer',
        sourceChannel: CHAIN_CONFIG[denom].depositChannel,
        token: {
          amount: amountInMicroDenom.toString(),
          denom: tokenizeSharesDenomToIbc
        },
        sender: selectedAddress,
        receiver: strideAddress,
        memo: '',
        timeoutTimestamp: generateIbcTimeoutTimestamp()
      }
    }

    const [client, account] = await Promise.all([getSigningStargateClient(), getAccount()])
    const ibcParams = { client, denom, account, offlineSigner: getOfflineSigner(), wallet: wallet.name }
    const gas = await simulateIbcTransaction2(ibcParams, selectedAddress, [msgTransfer], '')
    const fee = calculateFeeWithMinimalDenom(minimalDenom, gas)

    return await signIbcTransaction2(ibcParams, selectedAddress, [msgTransfer], fee, '')
  }

  const handleSuccess = (raw: IbcReturnType) => {
    // This is the only way to allow React v18 to update the state first before
    // the next mutation (which requires this state to have a value) is executed
    flushSync(() => {
      setIbcTransferRaw(raw)
    })
  }

  return useMutation({
    mutationFn: handleMutation,
    onSuccess: handleSuccess
  })
}

export { useSignIbcTransferMutation }
